import React, { useEffect } from "react";
import {
    Card, CardBody, CardHeader, Row, Col, Input, Label,
    FormGroup, Badge, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import { employeeSchema, employeeDefaults } from "../../../../models/employee";
import PropTypes from "prop-types";
import { FormPhotoUpload, LoadingButton, OverlayLoader } from "../../../../components/Common";
import { GENDER_OPTIONS } from "../../../../constants/gender";
import { GLOBAL_STATUS } from "../../../../constants/common";

/**
 * EmployeeEditor
 * Formulário principal de Colaboradores.
 */
const EmployeeEditor = ({ employee, roles, onSave, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: employeeDefaults,
        validationSchema: employeeSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado
    useEffect(() => {
        if (employee) {
            formik.resetForm({
                values: {
                    ...employeeDefaults,
                    ...employee,
                    address: {
                        ...employeeDefaults.address,
                        ...(employee.address || {})
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employee]);

    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!employee) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-card-account-details-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione um colaborador</h5>
                    <p className="text-muted">Escolha um colaborador na lista ao lado ou cadastre um novo para dar acessos ao sistema.</p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                <i className="mdi mdi-account-tie font-size-22"></i>
                            </span>
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{formik.values.id ? formik.values.fullName : "Novo Colaborador"}</span>
                                {formik.values.id && (
                                    <Badge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                        {formik.values.status === GLOBAL_STATUS.ACTIVE ? 'Ativo' : 'Inativo'}
                                    </Badge>
                                )}
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">Dados completos e Acessos</p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        {/* Se no futuro houver exclusão, colocariamos o botão aqio */}
                        <LoadingButton
                            loading={isSaving}
                            loadingText="Salvando..."
                            color="primary"
                            className="btn-md rounded-0 px-4 shadow-sm"
                            onClick={formik.handleSubmit}
                        >
                            Salvar
                        </LoadingButton>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4">
                <OverlayLoader show={isSaving} label="Salvando colaborador..." />
                <Row className="mb-4">
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-account-circle-outline me-2 font-size-16 text-primary"></i>
                            Perfil e Credenciais
                        </h6>
                    </Col>

                    <Col md="3" className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center">
                        <FormPhotoUpload
                            photoPreview={formik.values.photoUrl}
                            onChange={(url) => {
                                formik.setFieldValue("photoUrl", url);
                                onChange(true);
                            }}
                            size="lg"
                        />
                    </Col>

                    <Col md="9">
                        <Row>
                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Nome</Label>
                                    <Input
                                        name="firstName"
                                        type="text"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Ex: João"
                                        {...formik.getFieldProps("firstName")}
                                        invalid={formik.touched.firstName && !!formik.errors.firstName}
                                    />
                                    <FormFeedback>{formik.errors.firstName}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Sobrenome</Label>
                                    <Input
                                        name="lastName"
                                        type="text"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="Ex: da Silva"
                                        {...formik.getFieldProps("lastName")}
                                        invalid={formik.touched.lastName && !!formik.errors.lastName}
                                    />
                                    <FormFeedback>{formik.errors.lastName}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col md="4">
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Status</Label>
                                    <div className="form-check form-switch pt-2">
                                        <Input
                                            type="switch"
                                            role="switch"
                                            checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                            onChange={(e) => {
                                                formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                                onChange(true);
                                            }}
                                            className="form-check-input custom-switch-primary"
                                        />
                                        <Label className="ms-2 small text-muted">
                                            {formik.values.status === GLOBAL_STATUS.ACTIVE ? 'Acesso Liberado' : 'Acesso Bloqueado'}
                                        </Label>
                                    </div>
                                    <FormFeedback>{formik.errors.status}</FormFeedback>
                                </FormGroup>
                            </Col>

                            <Col md={formik.values.id ? "6" : "4"}>
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Email de Acesso</Label>
                                    <Input
                                        name="email"
                                        type="email"
                                        className="form-control bg-light-subtle border-0"
                                        placeholder="exemplo@gmail.com"
                                        {...formik.getFieldProps("email")}
                                        invalid={formik.touched.email && !!formik.errors.email}
                                        disabled={!!formik.values.id}
                                        title={!!formik.values.id ? "O e-mail de acesso não pode ser alterado por aqui" : ""}
                                    />
                                    <FormFeedback>{formik.errors.email}</FormFeedback>
                                </FormGroup>
                            </Col>

                            {!formik.values.id && (
                                <Col md="4">
                                    <FormGroup className="mb-3">
                                        <Label className="fw-medium text-muted small text-uppercase">Senha de Acesso</Label>
                                        <Input
                                            name="password"
                                            type="text"
                                            className="form-control bg-light-subtle border-0"
                                            placeholder="Crie uma senha forte"
                                            {...formik.getFieldProps("password")}
                                            invalid={formik.touched.password && !!formik.errors.password}
                                        />
                                        <FormFeedback>{formik.errors.password}</FormFeedback>
                                    </FormGroup>
                                </Col>
                            )}

                            <Col md={formik.values.id ? "6" : "4"}>
                                <FormGroup className="mb-3">
                                    <Label className="fw-medium text-muted small text-uppercase">Cargo / Permissões</Label>
                                    <Input
                                        name="role"
                                        type="select"
                                        className="form-select form-select bg-light-subtle border-0"
                                        {...formik.getFieldProps("role")}
                                        invalid={formik.touched.role && !!formik.errors.role}
                                    >
                                        <option value="">Selecione um Cargo</option>
                                        <option value="admin">Administrador (Root)</option>
                                        {roles?.map(role => (
                                            <option key={role.id} value={role.id}>{role.name}</option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{formik.errors.role}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <hr className="my-4 border-dashed" />

                <Row>
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-clipboard-account-outline me-2 font-size-16 text-primary"></i>
                            Dados Pessoais
                        </h6>
                    </Col>

                    <Col md="3">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">CPF</Label>
                            <Input
                                name="cpf"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="000.000.000-00"
                                {...formik.getFieldProps("cpf")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="3">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Telefone / WhatsApp</Label>
                            <Input
                                name="phone"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="(11) 90000-0000"
                                {...formik.getFieldProps("phone")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="3">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Gênero</Label>
                            <Input
                                name="gender"
                                type="select"
                                className="form-select form-select bg-light-subtle border-0"
                                {...formik.getFieldProps("gender")}
                            >
                                <option value="">Selecione</option>
                                {GENDER_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>

                    <Col md="3">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Data de Nascimento</Label>
                            <Input
                                name="birthDate"
                                type="date"
                                className="form-control bg-light-subtle border-0"
                                {...formik.getFieldProps("birthDate")}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <hr className="my-4 border-dashed" />

                <Row>
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-map-marker-outline me-2 font-size-16 text-primary"></i>
                            Endereço
                        </h6>
                    </Col>

                    <Col md="3">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">CEP</Label>
                            <Input
                                name="address.zipCode"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="00000-000"
                                {...formik.getFieldProps("address.zipCode")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="3">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Estado (UF)</Label>
                            <Input
                                name="address.state"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Ex: SP"
                                {...formik.getFieldProps("address.state")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="6">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Cidade</Label>
                            <Input
                                name="address.city"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Sua Cidade"
                                {...formik.getFieldProps("address.city")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="4">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Bairro</Label>
                            <Input
                                name="address.neighborhood"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Seu Bairro"
                                {...formik.getFieldProps("address.neighborhood")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="6">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Logradouro / Endereço</Label>
                            <Input
                                name="address.street"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Rua, Avenida..."
                                {...formik.getFieldProps("address.street")}
                            />
                        </FormGroup>
                    </Col>

                    <Col md="2">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Número</Label>
                            <Input
                                name="address.number"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Nº / S/N"
                                {...formik.getFieldProps("address.number")}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <hr className="my-4 border-dashed" />

                <Row>
                    <Col lg={12}>
                        <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                            <i className="mdi mdi-card-account-mail-outline me-2 font-size-16 text-primary"></i>
                            Informações Profissionais
                        </h6>
                    </Col>

                    <Col md="4">
                        <FormGroup className="mb-4">
                            <Label className="fw-medium text-muted small text-uppercase">Conselho de Classe (CREF, CRM...)</Label>
                            <Input
                                name="professionalRegistration"
                                type="text"
                                className="form-control bg-light-subtle border-0"
                                placeholder="Ex: 000000-G/SP"
                                {...formik.getFieldProps("professionalRegistration")}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

EmployeeEditor.propTypes = {
    employee: PropTypes.object,
    roles: PropTypes.array,
    onSave: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default EmployeeEditor;
